import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams, Link } from "react-router";
import { Spreekballon } from "../components/Spreekballon";
import { Button } from "../components/ui/Button";
import {
  signInWithEmail,
  renderGoogleSignInButton,
} from "../lib/auth-client";
import { useT } from "../lib/use-t";

export function LoginPage() {
  const t = useT();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Why: requireSession redirects to /login?next=<path>; honour it so the user
  // lands where they were headed. Fall back to "/". Guard against open-redirect
  // by only accepting same-origin absolute paths.
  const nextParam = searchParams.get("next");
  const dest = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function humanError(err: unknown): string {
    const code = (err as { code?: string }).code;
    const key = humanCodeErrorKey(code);
    if (key) return t(key);
    if (code) return t("login.errors.failedFmt", { code });
    return err instanceof Error ? err.message : t("chrome.common.unknownError");
  }

  async function onEmail(ev: FormEvent) {
    ev.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signInWithEmail(email, password);
      nav(dest, { replace: true });
    } catch (err) {
      setError(humanError(err));
      setSubmitting(false);
    }
  }

  const gsiContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!gsiContainerRef.current) return;
    const container = gsiContainerRef.current;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.google?.accounts?.id) {
        setTimeout(tryRender, 50);
        return;
      }
      renderGoogleSignInButton(container, {
        onSuccess: () => nav(dest, { replace: true }),
        onError: (err) => setError(humanError(err)),
      });
    };
    tryRender();
    return () => {
      cancelled = true;
    };
  }, [nav, dest]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-bg px-5 py-10">
      <div className="w-full max-w-sm space-y-8">
        <div className="space-y-3 text-center">
          <Spreekballon className="mx-auto h-12 w-12 text-[var(--accent)]" />
          <h1 className="text-2xl font-semibold tracking-tight">{t("login.title")}</h1>
          <p className="font-serif-italic text-[var(--ink-muted)]">{t("login.tagline")}</p>
        </div>

        <form onSubmit={onEmail} className="space-y-3">
          <input
            type="email" required placeholder={t("login.emailPlaceholder")} value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-[var(--panel-soft)] px-3 py-2 text-sm text-[var(--ink)] focus:border-[var(--accent)] focus:outline-2 focus:outline-[var(--accent)]"
          />
          <input
            type="password" required placeholder={t("login.passwordPlaceholder")} value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-[var(--panel-soft)] px-3 py-2 text-sm text-[var(--ink)] focus:border-[var(--accent)] focus:outline-2 focus:outline-[var(--accent)]"
          />
          <Button type="submit" variant="primary" size="lg" disabled={submitting} className="w-full">
            {submitting ? t("login.submitBusy") : t("login.submit")}
          </Button>
        </form>

        <div className="flex items-center gap-3">
          <span className="h-px flex-1 bg-[var(--border)]" />
          <span className="text-xs uppercase tracking-wider font-mono-badge text-[var(--ink-faint)]">{t("login.dividerOr")}</span>
          <span className="h-px flex-1 bg-[var(--border)]" />
        </div>

        <div ref={gsiContainerRef} className="flex justify-center" aria-label={t("login.googleAriaLabel")} />

        {error ? (
          <p role="alert" className="rounded-md border border-[var(--fail)] bg-[var(--fail)]/10 px-3 py-2 text-sm text-[var(--fail)]">
            {error}
          </p>
        ) : null}

        <div className="text-sm text-[var(--ink-muted)] text-center space-y-1">
          <Link to="/signup" className="block hover:text-[var(--ink)]">{t("login.createAccount")}</Link>
          <Link to="/forgot-password" className="block hover:text-[var(--ink)]">{t("login.forgotPassword")}</Link>
        </div>
      </div>
    </main>
  );
}

function humanCodeErrorKey(code: string | undefined): string | null {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "login.errors.invalidCredential";
    case "auth/too-many-requests":
      return "login.errors.tooManyRequests";
    case "auth/popup-closed-by-user":
      return "login.errors.popupClosedByUser";
    case "auth/account-exists-with-different-credential":
      return "login.errors.accountExistsWithDifferentCredential";
    case "auth/unauthorized-domain":
      return "login.errors.unauthorizedDomain";
    case "auth/cancelled-popup-request":
    case "auth/popup-blocked":
      return "login.errors.cancelledOrBlocked";
    default:
      return null;
  }
}
